import type {
  ApiBillingConfig,
  ApiError,
  ApiInvite,
  ApiInvitePreview,
  ApiMember,
  ApiProject,
  ApiSyncToken,
  ApiUser,
  ApiWorkspace,
  CreateProjectBody,
  MeResponse,
  ResetPasswordBody,
  SessionResponse,
  Plan,
  Role,
} from '@tomo/core/api';

/** REST client for Tomo Cloud (same origin; the dev server proxies `/api` and `/sync`). */

export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiError['error']['code'] | 'network',
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      method,
      credentials: 'include',
      // The server's CSRF guard requires JSON on every mutating request, even body-less ones.
      headers: method !== 'GET' ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiRequestError(0, 'network', 'Could not reach Tomo Cloud.');
  }
  if (!res.ok) {
    let err: ApiError | null = null;
    try {
      err = (await res.json()) as ApiError;
    } catch {
      /* not JSON (e.g. no server behind the static host) */
    }
    throw new ApiRequestError(res.status, err?.error?.code ?? (res.status === 404 ? 'not_found' : 'internal'), err?.error?.message ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) throw new ApiRequestError(res.status, 'internal', 'Unexpected response from server.');
  return (await res.json()) as T;
}

const enc = encodeURIComponent;

export const api = {
  health: () => request<{ ok: true; version: string }>('GET', '/health'),

  signup: (email: string, password: string, name: string) => request<{ user: ApiUser }>('POST', '/auth/signup', { email, password, name }),
  login: (email: string, password: string) => request<{ user: ApiUser }>('POST', '/auth/login', { email, password }),
  logout: () => request<{ ok: true }>('POST', '/auth/logout', {}),
  me: () => request<MeResponse>('GET', '/me'),
  session: () => request<SessionResponse>('GET', '/session'),
  updateMe: (patch: { name?: string; color?: string }) => request<{ user: ApiUser }>('PATCH', '/me', patch),
  forgotPassword: (email: string) => request<{ ok: true }>('POST', '/auth/forgot-password', { email }),
  resetPassword: (body: ResetPasswordBody) => request<{ user: ApiUser }>('POST', '/auth/reset-password', body),
  verifyEmail: (token: string) => request<{ ok: true; email: string }>('POST', '/auth/verify-email', { token }),
  resendVerification: () => request<{ ok: true }>('POST', '/auth/verify-email/resend', {}),

  workspaces: {
    list: () => request<ApiWorkspace[]>('GET', '/workspaces'),
    create: (name: string) => request<ApiWorkspace>('POST', '/workspaces', { name }),
    rename: (id: string, name: string) => request<ApiWorkspace>('PATCH', `/workspaces/${enc(id)}`, { name }),
    remove: (id: string) => request<{ ok: true }>('DELETE', `/workspaces/${enc(id)}`),
    members: (id: string) => request<ApiMember[]>('GET', `/workspaces/${enc(id)}/members`),
    setRole: (id: string, userId: string, role: Role) => request<ApiMember>('PATCH', `/workspaces/${enc(id)}/members/${enc(userId)}`, { role }),
    removeMember: (id: string, userId: string) => request<{ ok: true }>('DELETE', `/workspaces/${enc(id)}/members/${enc(userId)}`),
    invites: (id: string) => request<ApiInvite[]>('GET', `/workspaces/${enc(id)}/invites`),
    createInvite: (id: string, role: Exclude<Role, 'owner'>, email?: string) => request<ApiInvite>('POST', `/workspaces/${enc(id)}/invites`, { role, ...(email ? { email } : {}) }),
    revokeInvite: (id: string, inviteId: string) => request<{ ok: true }>('DELETE', `/workspaces/${enc(id)}/invites/${enc(inviteId)}`),
    projects: (id: string) => request<ApiProject[]>('GET', `/workspaces/${enc(id)}/projects`),
    createProject: (id: string, body: CreateProjectBody) => request<ApiProject>('POST', `/workspaces/${enc(id)}/projects`, body),
  },

  invites: {
    preview: (token: string) => request<ApiInvitePreview>('GET', `/invites/${enc(token)}`),
    accept: (token: string) => request<ApiWorkspace>('POST', `/invites/${enc(token)}/accept`, {}),
  },

  projects: {
    update: (id: string, patch: { name?: string; icon?: string | null; color?: string | null }) => request<ApiProject>('PATCH', `/projects/${enc(id)}`, patch),
    remove: (id: string) => request<{ ok: true }>('DELETE', `/projects/${enc(id)}`),
    syncToken: (id: string) => request<ApiSyncToken>('POST', `/projects/${enc(id)}/sync-token`, {}),
  },

  blobs: {
    check: (projectId: string, hashes: string[]) => request<{ missing: string[] }>('POST', `/projects/${enc(projectId)}/blobs/check`, { hashes }),
    async upload(projectId: string, hash: string, blob: Blob): Promise<void> {
      const res = await fetch(`${API_BASE}/api/projects/${enc(projectId)}/blobs/${enc(hash)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': blob.type || 'application/octet-stream' },
        body: blob,
      });
      if (!res.ok) throw new ApiRequestError(res.status, res.status === 413 ? 'too_large' : 'internal', `Upload failed (${res.status})`);
    },
    async download(projectId: string, hash: string): Promise<Blob | null> {
      const res = await fetch(`${API_BASE}/api/projects/${enc(projectId)}/blobs/${enc(hash)}`, { credentials: 'include' });
      return res.ok ? await res.blob() : null;
    },
  },

  billing: {
    config: () => request<ApiBillingConfig>('GET', '/billing/config'),
    checkout: (workspaceId: string) => request<{ url: string }>('POST', `/workspaces/${enc(workspaceId)}/billing/checkout`, {}),
    portal: (workspaceId: string) => request<{ url: string }>('POST', `/workspaces/${enc(workspaceId)}/billing/portal`, {}),
    devPlan: (workspaceId: string, plan: Plan) => request<ApiWorkspace>('POST', `/workspaces/${enc(workspaceId)}/billing/dev-plan`, { plan }),
  },
};
